import React from "react";
import moment from "moment";

import { useApolloClient } from "@apollo/client";

import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

/** contexts */
import { useSettings } from "../../../context/settings";

/** QUERIES */
import { MEDICINES_REPORT } from "../../../provider/Medicines/query";

/** utils */
import { getGraphsqErros } from "../../../utilities/functions";
import { exportMedicinesReportPdf } from "./exportPdf";

const activeOptions = [
    { label: "Ativos", value: true },
    { label: "Inativos", value: false },
    { label: "Todos", value: null },
];

const deletedOptions = [
    { label: "Não excluídos", value: false },
    { label: "Excluídos", value: true },
    { label: "Todos", value: null },
];

interface iMedicineFilters {
    search: string;
    due_date_start: Date | null;
    due_date_end: Date | null;
    only_not_expired: boolean;
    only_expired: boolean;
    only_in_stock: boolean;
    only_out_of_stock: boolean;
    is_actived: boolean | null;
    is_deleted: boolean | null;
    created_at_start: Date | null;
    created_at_end: Date | null;
}

const defaultFilters = (): iMedicineFilters => ({
    search: "",
    due_date_start: null,
    due_date_end: null,
    only_not_expired: true,
    only_expired: false,
    only_in_stock: false,
    only_out_of_stock: false,
    is_actived: true,
    is_deleted: false,
    created_at_start: moment("2026-01-01").toDate(),
    created_at_end: moment("2026-12-31").toDate(),
});

const getStatus = (record: iMedicine) => {
    if (!record.due_date) return "Sem vencimento";
    return moment(record.due_date).isBefore(moment(), "day") ? "Vencido" : "Em estoque";
};

const buildFilterSummary = (filters: iMedicineFilters) => {
    const summary: string[] = [];

    if (filters.search) summary.push(`Busca: ${filters.search}`);
    if (filters.due_date_start || filters.due_date_end) {
        summary.push(
            `Vencimento: ${filters.due_date_start ? moment(filters.due_date_start).format("DD/MM/YYYY") : "-"} a ${
                filters.due_date_end ? moment(filters.due_date_end).format("DD/MM/YYYY") : "-"
            }`
        );
    }
    if (filters.only_not_expired) summary.push("Apenas não vencidos");
    if (filters.only_expired) summary.push("Apenas vencidos");
    if (filters.only_in_stock) summary.push("Apenas em estoque");
    if (filters.only_out_of_stock) summary.push("Apenas sem estoque");
    if (filters.is_actived === true) summary.push("Ativos");
    if (filters.is_actived === false) summary.push("Inativos");
    if (filters.is_deleted === true) summary.push("Excluídos");
    if (filters.is_deleted === false) summary.push("Não excluídos");
    if (filters.created_at_start || filters.created_at_end) {
        summary.push(
            `Cadastrado entre: ${filters.created_at_start ? moment(filters.created_at_start).format("DD/MM/YYYY") : "-"} a ${
                filters.created_at_end ? moment(filters.created_at_end).format("DD/MM/YYYY") : "-"
            }`
        );
    }

    return summary;
};

const MedicinesReport = () => {
    const settings = useSettings();
    const apollo = useApolloClient();

    const toast = React.useRef<any>(null);

    const [records, setRecords] = React.useState<iMedicine[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [filters, setFilters] = React.useState<iMedicineFilters>(defaultFilters());
    const [sortField, setSortField] = React.useState<string>("name");
    const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">("asc");

    const buildFilterVariables = (currentFilters: iMedicineFilters) => ({
        search: currentFilters.search || undefined,
        due_date_start: currentFilters.due_date_start || undefined,
        due_date_end: currentFilters.due_date_end || undefined,
        only_not_expired: currentFilters.only_not_expired || undefined,
        only_expired: currentFilters.only_expired || undefined,
        only_in_stock: currentFilters.only_in_stock || undefined,
        only_out_of_stock: currentFilters.only_out_of_stock || undefined,
        is_actived: currentFilters.is_actived === null ? undefined : currentFilters.is_actived,
        is_deleted: currentFilters.is_deleted === null ? undefined : currentFilters.is_deleted,
        created_at_start: currentFilters.created_at_start || undefined,
        created_at_end: currentFilters.created_at_end || undefined,
    });

    const loadRecords = () => {
        setLoading(true);

        apollo
            .query({
                query: MEDICINES_REPORT,
                variables: {
                    filter: buildFilterVariables(filters),
                    sort: { field: sortField, direction: sortDirection },
                },
                fetchPolicy: "no-cache",
            })
            .then(response => {
                setRecords(response.data.medicines);
                setLoading(false);
            })
            .catch(error => {
                setRecords([]);
                settings?.setErrorMessage(getGraphsqErros(error));
                settings?.setVisibleError(true);
                setLoading(false);
            });
    };

    const onSort = (event: any) => {
        setSortField(event.sortField);
        setSortDirection(event.sortOrder === 1 ? "asc" : "desc");
    };

    const onExportPdf = () => {
        if (!records.length) {
            toast.current.show({ severity: "warn", summary: "Atenção", detail: "Nenhum registro para exportar.", life: 3000 });
            return;
        }

        exportMedicinesReportPdf(records, buildFilterSummary(filters), { field: sortField, direction: sortDirection });
    };

    React.useEffect(() => {
        loadRecords();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortField, sortDirection]);

    const nameBodyTemplate = (rowData: iMedicine) => (
        <>
            <span className="p-column-title">Nome</span>
            {rowData.name}
        </>
    );

    const dueDateBodyTemplate = (rowData: iMedicine) => (
        <>
            <span className="p-column-title">Vencimento</span>
            {rowData.due_date ? moment(rowData.due_date).utc().format("DD/MM/YYYY") : "-"}
        </>
    );

    const createdAtBodyTemplate = (rowData: iMedicine) => (
        <>
            <span className="p-column-title">Cadastrado em</span>
            {rowData.created_at ? moment(rowData.created_at).utc().format("DD/MM/YYYY") : "-"}
        </>
    );

    const statusBodyTemplate = (rowData: iMedicine) => (
        <>
            <span className="p-column-title">Status</span>
            {getStatus(rowData)}
        </>
    );

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{loading ? "Carregando relatório..." : "Relatório de Medicamentos"}</h5>
        </div>
    );

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />

                    <h5 className="mb-4">Filtros</h5>

                    <div className="flex flex-column md:flex-row flex-wrap">
                        <div className="field col-12 md:col-4">
                            <label htmlFor="search">Buscar (nome ou caixa)</label>
                            <InputText
                                id="search"
                                className="p-3"
                                value={filters.search}
                                onChange={e => setFilters({ ...filters, search: e.target.value })}
                            />
                        </div>

                        <div className="field col-12 md:col-4">
                            <label htmlFor="due_date">Vencimento entre</label>
                            <Calendar
                                id="due_date"
                                selectionMode="range"
                                dateFormat="dd/mm/yy"
                                value={[filters.due_date_start, filters.due_date_end] as any}
                                onChange={e => {
                                    const [start, end] = (e.value as any) || [null, null];
                                    setFilters({ ...filters, due_date_start: start, due_date_end: end });
                                }}
                            />
                        </div>

                        <div className="field col-12 md:col-4">
                            <label htmlFor="created_at">Cadastrado entre</label>
                            <Calendar
                                id="created_at"
                                selectionMode="range"
                                dateFormat="dd/mm/yy"
                                value={[filters.created_at_start, filters.created_at_end] as any}
                                onChange={e => {
                                    const [start, end] = (e.value as any) || [null, null];
                                    setFilters({ ...filters, created_at_start: start, created_at_end: end });
                                }}
                            />
                        </div>
                    </div>

                    <div className="flex flex-column md:flex-row flex-wrap align-items-center">
                        <div className="field col-12 md:col-3 flex align-items-center">
                            <Checkbox
                                inputId="only_not_expired"
                                checked={filters.only_not_expired}
                                onChange={e => setFilters({ ...filters, only_not_expired: !!e.checked, only_expired: false })}
                            />
                            <label htmlFor="only_not_expired" className="ml-2">
                                Somente não vencidos
                            </label>
                        </div>

                        <div className="field col-12 md:col-3 flex align-items-center">
                            <Checkbox
                                inputId="only_expired"
                                checked={filters.only_expired}
                                onChange={e => setFilters({ ...filters, only_expired: !!e.checked, only_not_expired: false })}
                            />
                            <label htmlFor="only_expired" className="ml-2">
                                Somente vencidos
                            </label>
                        </div>

                        <div className="field col-12 md:col-3 flex align-items-center">
                            <Checkbox
                                inputId="only_in_stock"
                                checked={filters.only_in_stock}
                                onChange={e => setFilters({ ...filters, only_in_stock: !!e.checked, only_out_of_stock: false })}
                            />
                            <label htmlFor="only_in_stock" className="ml-2">
                                Somente em estoque
                            </label>
                        </div>

                        <div className="field col-12 md:col-3 flex align-items-center">
                            <Checkbox
                                inputId="only_out_of_stock"
                                checked={filters.only_out_of_stock}
                                onChange={e => setFilters({ ...filters, only_out_of_stock: !!e.checked, only_in_stock: false })}
                            />
                            <label htmlFor="only_out_of_stock" className="ml-2">
                                Somente sem estoque
                            </label>
                        </div>
                    </div>

                    <div className="flex flex-column md:flex-row flex-wrap">
                        <div className="field col-12 md:col-4">
                            <label htmlFor="is_actived">Status</label>
                            <Dropdown
                                id="is_actived"
                                options={activeOptions}
                                value={filters.is_actived}
                                onChange={e => setFilters({ ...filters, is_actived: e.value })}
                            />
                        </div>

                        <div className="field col-12 md:col-4">
                            <label htmlFor="is_deleted">Exclusão</label>
                            <Dropdown
                                id="is_deleted"
                                options={deletedOptions}
                                value={filters.is_deleted}
                                onChange={e => setFilters({ ...filters, is_deleted: e.value })}
                            />
                        </div>
                    </div>

                    <div className="flex justify-content-end mb-4">
                        <Button label="Limpar filtros" icon="pi pi-times" className="p-button-text mr-2" onClick={() => setFilters(defaultFilters())} />
                        <Button label="Aplicar filtros" icon="pi pi-filter" className="p-button-info mr-2" onClick={loadRecords} />
                        <Button label="Exportar PDF" icon="pi pi-file-pdf" className="p-button-success" onClick={onExportPdf} />
                    </div>

                    <DataTable
                        value={records}
                        dataKey="id"
                        paginator
                        stripedRows={true}
                        rowHover
                        showGridlines
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
                        emptyMessage="Nenhum registro encontrado"
                        header={header}
                        sortField={sortField}
                        sortOrder={sortDirection === "asc" ? 1 : -1}
                        onSort={onSort}
                        responsiveLayout="scroll">
                        <Column field="name" header="Nome" sortable body={nameBodyTemplate}></Column>
                        <Column field="need" header="Necessidade" sortable></Column>
                        <Column field="stock" header="Estoque" sortable></Column>
                        <Column field="box" header="Caixa" sortable></Column>
                        <Column field="due_date" header="Vencimento" sortable body={dueDateBodyTemplate}></Column>
                        <Column field="created_at" header="Cadastrado em" sortable body={createdAtBodyTemplate}></Column>
                        <Column header="Status" body={statusBodyTemplate}></Column>
                    </DataTable>
                </div>
            </div>
        </div>
    );
};

export default MedicinesReport;
